/**
 * Zod schemas for the session domain's wire shapes, served by
 * tricorder-session through the gateway's /sessions/** proxy from the
 * cut-over on. Mirrors types_session.ts (tricorder-session SPEC.md
 * sections 2, 3, 7).
 */

import { z } from 'zod';

const ActorRefSchema = z.object({
  performer: z.string(),
  on_behalf_of: z.string().nullable().optional(),
});

const PinnedAgentSchema = z.object({
  slug: z.string(),
  version: z.string(),
});

const SubjectRefSchema = z.object({
  type: z.string(),
  id: z.string(),
});

export const SessionResponseSchema = z.object({
  id: z.string(),
  // Absent on a session that pins no agent: a protocol run
  // (tricorder-session/ADR-0009), which tricorder-session serves without it.
  agent: PinnedAgentSchema.optional(),
  subject: SubjectRefSchema.nullable().optional(),
  parent: z.string().nullable().optional(),
  status: z.enum(['active', 'archived']),
  title: z.string(),
  summary: z.string(),
  created_by: ActorRefSchema,
  created_at: z.string(),
  updated_at: z.string(),
  archived_at: z.string().nullable().optional(),
});

export const MessageResponseSchema = z.object({
  id: z.string(),
  session: z.string(),
  seq: z.number(),
  type: z.string(),
  role: z.enum(['user', 'agent']).nullable().optional(),
  author: ActorRefSchema,
  payload: z.unknown(),
  created_at: z.string(),
});

function sessionPageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
  });
}

export const MessageResponsePageSchema = sessionPageSchema(MessageResponseSchema);

export const CreateSessionResponseSchema = z.object({
  session: SessionResponseSchema,
  first_message: MessageResponseSchema,
});

// An agentless create's 201 (tricorder-session/ADR-0009): the session, and no
// first message, because no turn runs on it.
export const CreateAgentlessSessionResponseSchema = z.object({
  session: SessionResponseSchema,
});

// A step message's payload (tricorder-protos schemas/protocol/step.cue at
// v1.20.0): the signed state token is the state; the ids beside it are a
// reader's view.
export const ProtocolStepPayloadSchema = z.object({
  state_token: z.string().min(1),
  from_node_id: z.string().optional(),
  to_node_id: z.string(),
  action_id: z.string().optional(),
  protocol_id: z.string().optional(),
  protocol_version: z.string().optional(),
  observation: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])).optional(),
});
